import React from 'react';
import './DataStructures.css'; // Re-using the same CSS for now

const Threading = () => {
  return (
    <div className="data-structure-section">
      <h2>Threading in Java</h2>
      <p>
        Concurrency and multi-threading are critical topics for high-performance applications, especially in the finance sector where low latency is a key requirement.
      </p>

      <div className="topic-section">
        <h3>Creating a Thread</h3>
        <p>There are two primary ways to create a thread in Java: extending `Thread` or implementing `Runnable`. Implementing `Runnable` is generally preferred.</p>
        <div className="code-snippet">
{`// Using a lambda with the Runnable interface (common practice)
Runnable runnable = () -> System.out.println("Thread running with a lambda.");
Thread thread = new Thread(runnable);
thread.start();`}
        </div>
      </div>

      <div className="topic-section">
        <h3>The `synchronized` Keyword</h3>
        <p>
          The `synchronized` keyword provides a basic locking mechanism to prevent race conditions. However, it's a blunt instrument and can lead to performance bottlenecks.
        </p>
        <div className="code-snippet">
{`class Counter {
    private int count = 0;
    // Only one thread can execute this method on an instance of Counter at a time
    public synchronized void increment() {
        count++;
    }
}`}
        </div>
      </div>

      <div className="topic-section">
        <h3>The `volatile` Keyword</h3>
        <p>
          In a multi-threaded context, a thread may have a cached copy of a variable. The `volatile` keyword ensures that reads of a variable are always from main memory and writes go straight to main memory. It guarantees visibility, but not atomicity.
        </p>
        <div className="code-snippet">
{`class Status {
    // Guarantees that changes to 'running' are visible to all threads.
    private volatile boolean running = true;

    public void stop() {
        running = false;
    }

    public void work() {
        while(running) {
            // do work
        }
    }
}`}
        </div>
        <p>A common interview topic is the difference between `volatile` and `synchronized`. `volatile` is about visibility, `synchronized` is about atomicity and visibility. `volatile` is cheaper but has weaker guarantees.</p>
      </div>

      <div className="topic-section">
        <h3>ExecutorService and Thread Pools</h3>
        <p>
          Creating threads is expensive. A thread pool is a collection of pre-started threads that can be reused. `ExecutorService` is a high-level API for managing thread pools. This is the standard way to manage threads in modern Java applications.
        </p>
        <div className="code-snippet">
{`import java.util.concurrent.ExecutorService;
import java.util.concurrent.Executors;

// Create a fixed-size thread pool with 5 threads
ExecutorService executor = Executors.newFixedThreadPool(5);

// Submit tasks to the pool
for (int i = 0; i < 10; i++) {
    Runnable task = () -> System.out.println("Executing task in thread " + Thread.currentThread().getName());
    executor.submit(task);
}

// It's important to shut down the executor when it's no longer needed.
executor.shutdown();`}
        </div>
        <p>There are different types of thread pools (fixed, cached, single-threaded). Knowing which one to use is important.</p>
      </div>

      <div className="topic-section">
        <h3>CompletableFuture</h3>
        <p>
          `CompletableFuture` (Java 8+) is used for asynchronous programming. It represents a future result of an asynchronous computation. You can chain operations to be performed on the result without blocking the main thread. This is crucial for building responsive, non-blocking I/O-bound applications.
        </p>
        <div className="code-snippet">
{`import java.util.concurrent.CompletableFuture;
import java.util.concurrent.TimeUnit;

CompletableFuture<String> future = CompletableFuture.supplyAsync(() -> {
    // Simulate a long-running task
    try {
        TimeUnit.SECONDS.sleep(2);
    } catch (InterruptedException e) {
        throw new IllegalStateException(e);
    }
    return "Result of the asynchronous computation";
});

// You can do other things here, the above task is running in the background.

// Attach a callback to be executed when the future completes.
future.thenAccept(result -> System.out.println("Callback received: " + result));

System.out.println("Main thread is not blocked.");
// The program will exit before the future completes without a join() or sleep.`}
        </div>
        <p>Understanding how to use `supplyAsync`, `thenApply`, `thenAccept`, and `thenCombine` is key for modern Java concurrency interviews.</p>
      </div>
    </div>
  );
};

export default Threading;
